/**
 * Sales Teams & Lists (`docs/UTILITIES.md` §9).
 *
 * Two kinds of list, each edited and saved on its own:
 *
 * * **Sales teams** -- a manager, their email, and their reps. Sales Rep
 *   Performance offers each team as a filter, and the rep mail-out copies a
 *   rep's manager.
 * * **Email lists** -- who gets an emailed report (To or CC) and when.
 *
 * The lists live in `sync_hub`, so the workstation and the cloud portal show
 * the same ones. The server checks every rule (a rep in one team only, names
 * as the sales data spells them, real addresses); this page shows what it says.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Badge, Button, Card, ConfirmDialog, TextField } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { ApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import type { UtilityProps } from '../index'
import type { Landing, ListType, Member, PortalList } from './api'
import { listsApi } from './api'
import { Sending } from './Sending'

const LANDING_KEY = ['utility', 'sales-lists'] as const

const SELECT =
  'mt-1 w-full rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1.5 text-[length:var(--text-sm)]'

function blank(type: ListType): PortalList {
  return {
    list_id: null,
    list_type: type,
    name: '',
    owner_name: null,
    owner_email: null,
    attributes: type === 'email_list' ? { schedule: '1st_and_15th' } : {},
    members: [],
    updated_at: null,
    updated_by: null,
  }
}

const orNull = (value: string) => (value.trim() === '' ? null : value)

export function SalesLists(_props: UtilityProps) {
  const landing = useQuery({ queryKey: LANDING_KEY, queryFn: listsApi.landing })
  const [drafts, setDrafts] = useState<PortalList[]>([])

  if (landing.isPending) return <Skeleton rows={4} />
  if (landing.data === undefined) {
    return <ErrorState error={landing.error} onRetry={() => void landing.refetch()} />
  }
  const data = landing.data
  const all = [...data.lists, ...drafts]
  const teams = all.filter((list) => list.list_type === 'sales_team')
  const emails = all.filter((list) => list.list_type === 'email_list')
  const placed = new Set(teams.flatMap((team) => team.members.map((m) => m.member)))
  const unplaced = data.reps.filter((rep) => !placed.has(rep))
  const dropDraft = (draft: PortalList) => {
    setDrafts((current) => current.filter((item) => item !== draft))
  }

  return (
    <div className="space-y-6">
      <Card
        title="Sales teams"
        description="A manager and their reps. Sales Rep Performance offers each team as a filter, and a rep's mail-out copies their manager. A rep belongs to one team."
        actions={
          <Button
            onClick={() => {
              setDrafts((current) => [...current, blank('sales_team')])
            }}
          >
            <Plus aria-hidden className="size-3.5" /> New team
          </Button>
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          {teams.map((team) => (
            <ListEditor
              key={team.list_id ?? `draft-${String(drafts.indexOf(team))}`}
              list={team}
              landing={data}
              takenReps={placed}
              onDiscard={team.list_id === null ? () => { dropDraft(team) } : undefined}
              onSaved={team.list_id === null ? () => { dropDraft(team) } : undefined}
            />
          ))}
        </div>
        {unplaced.length > 0 && (
          <p className="mt-4 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
            <strong>Not in a team:</strong> {unplaced.join(', ')}. Their sales still count in
            the company totals; they appear under no team&apos;s filter.
          </p>
        )}
      </Card>

      <Card
        title="Email lists"
        description="Who gets an emailed report, and when. The Month-End Forecast email goes to the list of that name."
        actions={
          <Button
            onClick={() => {
              setDrafts((current) => [...current, blank('email_list')])
            }}
          >
            <Plus aria-hidden className="size-3.5" /> New list
          </Button>
        }
      >
        <div className="grid gap-4 lg:grid-cols-2">
          {emails.map((list) => (
            <ListEditor
              key={list.list_id ?? `draft-${String(drafts.indexOf(list))}`}
              list={list}
              landing={data}
              takenReps={placed}
              onDiscard={list.list_id === null ? () => { dropDraft(list) } : undefined}
              onSaved={list.list_id === null ? () => { dropDraft(list) } : undefined}
            />
          ))}
        </div>
      </Card>

      <Sending />
    </div>
  )
}

function ListEditor({
  list,
  landing,
  takenReps,
  onDiscard,
  onSaved,
}: {
  list: PortalList
  landing: Landing
  takenReps: ReadonlySet<string>
  onDiscard: (() => void) | undefined
  onSaved: (() => void) | undefined
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<PortalList>(list)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const isTeam = draft.list_type === 'sales_team'
  const dirty = JSON.stringify(draft) !== JSON.stringify(list)

  const save = useMutation({
    mutationFn: () => listsApi.save(draft),
    onSuccess: (result) => {
      setDraft(result.list)
      onSaved?.()
      void queryClient.invalidateQueries({ queryKey: LANDING_KEY })
    },
  })
  const remove = useMutation({
    mutationFn: () => listsApi.remove(list),
    onSuccess: () => {
      setConfirmDelete(false)
      void queryClient.invalidateQueries({ queryKey: LANDING_KEY })
    },
  })
  const fields = save.error instanceof ApiError ? save.error.fields : {}
  const setMember = (index: number, change: Partial<Member>) => {
    setDraft((current) => ({
      ...current,
      members: current.members.map((m, i) => (i === index ? { ...m, ...change } : m)),
    }))
  }
  const removeMember = (index: number) => {
    setDraft((current) => ({ ...current, members: current.members.filter((_m, i) => i !== index) }))
  }
  const addMember = (member: Member) => {
    setDraft((current) => ({ ...current, members: [...current.members, member] }))
  }
  const ownReps = new Set(list.members.map((m) => m.member))
  const available = landing.reps.filter(
    (rep) => (!takenReps.has(rep) || ownReps.has(rep)) && !draft.members.some((m) => m.member === rep),
  )

  return (
    <section
      className="rounded-[var(--radius-md)] border border-[var(--color-rule)] p-3"
      aria-label={draft.name === '' ? 'New list' : draft.name}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <TextField
          label={isTeam ? 'Team' : 'List'}
          value={draft.name}
          error={fields['name']}
          onChange={(event) => {
            setDraft({ ...draft, name: event.target.value })
          }}
        />
        {isTeam ? (
          <TextField
            label="Manager"
            value={draft.owner_name ?? ''}
            error={fields['owner_name']}
            onChange={(event) => {
              setDraft({ ...draft, owner_name: orNull(event.target.value) })
            }}
          />
        ) : (
          <label className="block">
            <span className="block text-[length:var(--text-xs)] font-medium">Sent</span>
            <select
              className={SELECT}
              value={draft.attributes['schedule'] ?? ''}
              onChange={(event) => {
                setDraft({ ...draft, attributes: { ...draft.attributes, schedule: event.target.value } })
              }}
            >
              {landing.schedules.map((schedule) => (
                <option key={schedule.value} value={schedule.value}>
                  {schedule.label}
                </option>
              ))}
            </select>
            {fields['schedule'] !== undefined && (
              <span className="mt-1 block text-[length:var(--text-2xs)] text-[var(--color-negative)]">
                {fields['schedule']}
              </span>
            )}
          </label>
        )}
        {isTeam && (
          <TextField
            label="Manager's email"
            type="email"
            value={draft.owner_email ?? ''}
            error={fields['owner_email']}
            onChange={(event) => {
              setDraft({ ...draft, owner_email: orNull(event.target.value) })
            }}
          />
        )}
      </div>

      <table className="mt-3 w-full text-[length:var(--text-sm)]">
        <thead>
          <tr className="text-left text-[length:var(--text-2xs)] tracking-wide text-[var(--color-ink-muted)] uppercase">
            <th scope="col" className="py-1 pr-2 font-medium">
              {isTeam ? 'Rep' : 'Name or email'}
            </th>
            <th scope="col" className="py-1 pr-2 font-medium">
              {isTeam ? "Rep's email" : 'Email'}
            </th>
            {!isTeam && (
              <th scope="col" className="py-1 pr-2 font-medium">
                To / CC
              </th>
            )}
            <th scope="col" className="sr-only">
              Remove
            </th>
          </tr>
        </thead>
        <tbody>
          {draft.members.map((member, index) => {
            const problem = fields[`member:${member.member}`]
            return (
              <tr key={index} className="align-top">
                <td className="py-1 pr-2">
                  {isTeam ? (
                    <span className="font-medium">{member.member}</span>
                  ) : (
                    <input
                      aria-label="Name or email"
                      className={SELECT}
                      value={member.member}
                      onChange={(event) => {
                        setMember(index, { member: event.target.value })
                      }}
                    />
                  )}
                  {problem !== undefined && (
                    <span className="block text-[length:var(--text-2xs)] text-[var(--color-negative)]">
                      {problem}
                    </span>
                  )}
                </td>
                <td className="py-1 pr-2">
                  <input
                    aria-label={`${member.member} email`}
                    type="email"
                    className={SELECT}
                    value={member.member_email ?? ''}
                    onChange={(event) => {
                      setMember(index, { member_email: orNull(event.target.value) })
                    }}
                  />
                </td>
                {!isTeam && (
                  <td className="py-1 pr-2">
                    <select
                      aria-label={`${member.member} To or CC`}
                      className={SELECT}
                      value={member.recipient ?? ''}
                      onChange={(event) => {
                        setMember(index, { recipient: orNull(event.target.value) })
                      }}
                    >
                      <option value="">Choose</option>
                      <option value="to">To</option>
                      <option value="cc">CC</option>
                    </select>
                  </td>
                )}
                <td className="py-1 text-right">
                  <button
                    type="button"
                    className="p-1 text-[var(--color-ink-muted)] hover:text-[var(--color-negative)]"
                    aria-label={`Remove ${member.member}`}
                    onClick={() => {
                      removeMember(index)
                    }}
                  >
                    <Trash2 aria-hidden className="size-3.5" />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="mt-2">
        {isTeam ? (
          <select
            aria-label="Add a rep"
            className={`${SELECT} max-w-[16rem]`}
            value=""
            onChange={(event) => {
              if (event.target.value !== '') {
                addMember({ member: event.target.value, member_email: null, recipient: null })
              }
            }}
          >
            <option value="">Add a rep…</option>
            {available.map((rep) => (
              <option key={rep} value={rep}>
                {rep}
              </option>
            ))}
          </select>
        ) : (
          <Button
            onClick={() => {
              addMember({ member: '', member_email: null, recipient: 'to' })
            }}
          >
            <Plus aria-hidden className="size-3.5" /> Add a person
          </Button>
        )}
      </div>

      {save.error !== null && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]">
          {save.error.message}
        </p>
      )}
      {remove.error !== null && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]">
          {remove.error.message}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate()
          }}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        {dirty && list.list_id !== null && (
          <Button
            onClick={() => {
              setDraft(list)
              save.reset()
            }}
          >
            Undo changes
          </Button>
        )}
        {onDiscard !== undefined && <Button onClick={onDiscard}>Discard</Button>}
        {list.list_id !== null && (
          <Button
            variant="danger"
            onClick={() => {
              setConfirmDelete(true)
            }}
          >
            Delete
          </Button>
        )}
        {dirty && <Badge tone="warning">Not saved</Badge>}
        {list.updated_at !== null && (
          <span className="ml-auto text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
            Saved {formatDateTime(list.updated_at)} by {list.updated_by}
          </span>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${list.name}?`}
        body={
          isTeam
            ? 'Its reps will be in no team until you add them to another. Their sales are not changed.'
            : 'Nobody will get this email until the list is set up again.'
        }
        confirmLabel="Delete"
        onConfirm={() => {
          remove.mutate()
        }}
        onCancel={() => {
          setConfirmDelete(false)
        }}
      />
    </section>
  )
}
